import React, { useState } from 'react';
import {
  CourseConcept,
  SubRubricDimension,
} from '../data/api';

interface EvaluationAnchorsProps {
  dimension: SubRubricDimension;
  onChange: (updates: Partial<SubRubricDimension>) => void;
  disabled?: boolean;
  readOnly?: boolean;
}

const EvaluationAnchors: React.FC<EvaluationAnchorsProps> = ({
  dimension,
  onChange,
  disabled = false,
  readOnly = false,
}) => {
  const criteria = dimension.observable_criteria ?? [];
  const concepts = dimension.course_concepts ?? [];
  const excerpts = dimension.source_excerpts ?? [];
  const [pendingRemoval, setPendingRemoval] = useState<string | null>(null);

  return (
    <div className="lti-tools-tab__evaluation-anchors">
      <div className="lti-tools-tab__evaluation-anchor-group">
        <p className="lti-tools-tab__evaluation-anchor-heading">Observable evaluation criteria</p>
        {criteria.map((criterion, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={index} className="lti-tools-tab__evaluation-anchor-field">
            <label htmlFor={`criterion-${dimension.name}-${index}`}>Criterion {index + 1}</label>
            <textarea
              id={`criterion-${dimension.name}-${index}`}
              className="form-control lti-tools-tab__compact-control"
              rows={2}
              value={criterion}
              onChange={(event) => onChange({
                observable_criteria: criteria.map((value, i) => (i === index ? event.target.value : value)),
              })}
              disabled={disabled}
              readOnly={readOnly}
            />
            {!readOnly && <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setPendingRemoval(`criterion-${index}`)} disabled={disabled}>Remove</button>}
            {!readOnly && pendingRemoval === `criterion-${index}` && (
            <span className="lti-tools-tab__inline-confirmation" role="status">
              Remove this criterion?
              <button type="button" className="btn btn-sm btn-link" onClick={() => setPendingRemoval(null)}>Cancel</button>
              <button
                type="button"
                className="btn btn-sm btn-danger"
                onClick={() => {
                  onChange({ observable_criteria: criteria.filter((_, i) => i !== index) });
                  setPendingRemoval(null);
                }}
              >Remove
              </button>
            </span>
            )}
          </div>
        ))}
        {!readOnly && <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => onChange({ observable_criteria: [...criteria, ''] })} disabled={disabled}>+ Add criterion</button>}
      </div>
      <div className="lti-tools-tab__evaluation-anchor-group">
        <p className="lti-tools-tab__evaluation-anchor-heading">Course concepts and definitions</p>
        {concepts.map((concept: CourseConcept, index) => (
          <div className="lti-tools-tab__evaluation-anchor-field" key={concept.name}>
            <label htmlFor={`concept-${dimension.name}-${index}`}>Concept {index + 1}</label>
            <div className="lti-tools-tab__evaluation-concept-inputs">
              <input
                id={`concept-${dimension.name}-${index}`}
                className="form-control lti-tools-tab__compact-control"
                value={concept.name}
                onChange={(event) => onChange({
                  course_concepts: concepts.map((value, i) => (
                    i === index ? { ...value, name: event.target.value } : value
                  )),
                })}
                disabled={disabled}
                readOnly={readOnly}
              />
              <textarea
                aria-label={`Concept ${index + 1} definition`}
                className="form-control lti-tools-tab__compact-control"
                rows={3}
                value={concept.definition}
                onChange={(event) => onChange({
                  course_concepts: concepts.map((value, i) => (
                    i === index ? { ...value, definition: event.target.value } : value
                  )),
                })}
                disabled={disabled}
                readOnly={readOnly}
              />
            </div>
            {!readOnly && <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setPendingRemoval(`concept-${index}`)} disabled={disabled}>Remove</button>}
            {!readOnly && pendingRemoval === `concept-${index}` && (
            <span className="lti-tools-tab__inline-confirmation" role="status">
              Remove this concept?
              <button type="button" className="btn btn-sm btn-link" onClick={() => setPendingRemoval(null)}>Cancel</button>
              <button
                type="button"
                className="btn btn-sm btn-danger"
                onClick={() => {
                  onChange({ course_concepts: concepts.filter((_, i) => i !== index) });
                  setPendingRemoval(null);
                }}
              >Remove
              </button>
            </span>
            )}
          </div>
        ))}
        {!readOnly && <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => onChange({ course_concepts: [...concepts, { name: '', definition: '' }] })} disabled={disabled}>+ Add concept</button>}
      </div>
      <div className="lti-tools-tab__evaluation-anchor-group">
        <p className="lti-tools-tab__evaluation-anchor-heading">Supporting course excerpts</p>
        {excerpts.map((excerpt, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={index} className="lti-tools-tab__evaluation-anchor-field">
            <label htmlFor={`excerpt-${dimension.name}-${index}`}>Excerpt {index + 1}</label>
            <textarea
              id={`excerpt-${dimension.name}-${index}`}
              className="form-control lti-tools-tab__compact-control"
              rows={3}
              value={excerpt.text}
              onChange={(event) => onChange({
                source_excerpts: excerpts.map((value, i) => (
                  i === index ? { ...value, text: event.target.value } : value
                )),
              })}
              disabled={disabled}
              readOnly={readOnly}
            />
            {!readOnly && <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setPendingRemoval(`excerpt-${index}`)} disabled={disabled}>Remove</button>}
            <span className="lti-tools-tab__excerpt-citation">
              <code>Sequential: {excerpt.sequential_id || 'not cited'}</code>
              <code>Vertical: {excerpt.vertical_id || 'not cited'}</code>
            </span>
            {!readOnly && pendingRemoval === `excerpt-${index}` && (
              <span className="lti-tools-tab__inline-confirmation" role="status">
                Remove this excerpt?
                <button type="button" className="btn btn-sm btn-link" onClick={() => setPendingRemoval(null)}>Cancel</button>
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  onClick={() => {
                    onChange({ source_excerpts: excerpts.filter((_, i) => i !== index) });
                    setPendingRemoval(null);
                  }}
                >Remove
                </button>
              </span>
            )}
          </div>
        ))}
        {!readOnly && (
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => onChange({
              source_excerpts: [...excerpts, { text: '', sequential_id: '', vertical_id: '' }],
            })}
            disabled={disabled}
          >
            + Add excerpt
          </button>
        )}
      </div>
    </div>
  );
};

export default EvaluationAnchors;
